/** /my/classes and /my/progress — the two destinations a signed-up student gets beside the
 * live screen (STORY-113 part 3).
 *
 * Both read the student's OWN record and nothing else: `/analytics/v1/me/*` is keyed on the
 * caller's subject, so there is no shape of request that could return a classmate. The
 * `report_release` gate is the service's, not this screen's — a course that releases per
 * session is absent until its teacher released one, and a `guest_only` course never mints a
 * subject at all, so it cannot appear here however long the student attends. The screens say
 * which of those is happening instead of showing an empty box.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useSurface } from "../platform/surface";
import { card, registrations } from "../live/studentIdentity";
import { BAND_GLYPH as glyph, BAND_TONE as tone, meGet, useMine } from "../student/mine";
import { BottomNav } from "../student/BottomNav";

type Assignment = { assignment_id: string; course_id: string; course_title: string; title: string; due_date: string | null; completed_at: string | null; items: { item_id: string }[]; answers: Record<string, unknown> };

function Shell({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  useSurface("student");
  return (
    <main className="font-system mx-auto flex min-h-dvh max-w-md flex-col gap-4 p-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-[color:var(--ink-2)]">{lede}</p>
      </header>
      {children}
      <BottomNav />
    </main>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">{children}</p>;
}

export function MyClasses() {
  const { t, i18n } = useTranslation();
  const mine = useMine();
  const work = useQuery({ queryKey: ["my", "assignments"], queryFn: () => meGet<Assignment[]>("/analytics/v1/me/assignments"), retry: false });
  const mySelf = card();
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));
  // The sign-up record is per course but arrives per session, and the service answers with
  // ids; the released mastery read is the only thing that knows a course's title, so the two
  // are joined here. One row per course, whichever session the sign-up happened in.
  const titles = new Map((mine.data?.courses ?? []).map((c) => [c.course_id, c.title]));
  const regs = [...new Map(registrations().map((r) => [r.course_id ?? r.session_id, r])).values()];

  return (
    <Shell title={t("my.classes.title")} lede={t("my.classes.lede")}>
      {mySelf && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm" aria-labelledby="my-registrations">
          <h2 id="my-registrations" className="font-semibold">
            {t("my.classes.onDevice")}
          </h2>
          <p className="mt-1 text-[color:var(--ink-2)]">{t("my.classes.onDeviceLede", { name: mySelf.display_name })}</p>
          <ul className="mt-2 flex flex-col gap-1">
            {regs.map((r) => (
              <li key={r.session_id} className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="min-w-0 break-words">
                  {r.course_title || (r.course_id && titles.get(r.course_id)) || t("my.classes.untitled")}
                </span>
                <span className="text-xs text-[color:var(--ink-2)]">
                  {r.status === "pending" ? t("my.classes.waiting") : t("my.classes.registered", { date: fmt(r.registered_at) })}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3">
            <Link to="/my/transfer" className="inline-flex min-h-[var(--control-h)] items-center underline">
              {t("my.classes.transfer")}
            </Link>
          </p>
        </section>
      )}

      {mine.isPending && <Note>{t("app.loading")}</Note>}
      {mine.isError && <Note>{t("my.classes.noPrincipal")}</Note>}
      {mine.data?.courses.length === 0 && <Note>{t("my.classes.empty")}</Note>}

      {mine.data?.courses.map((c) => {
        const answers = c.los.reduce((n, lo) => n + lo.n, 0);
        const assessed = c.los.filter((lo) => lo.n > 0).length;
        const hw = (work.data ?? []).filter((a) => a.course_id === c.course_id);
        const doneHw = hw.filter((a) => a.completed_at).length;
        return (
          <section key={c.course_id} className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
            <h2 className="text-lg font-semibold break-words">{c.title || t("my.classes.untitled")}</h2>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <dt className="text-[color:var(--ink-2)]">{t("my.classes.answers")}</dt>
              <dd className="num text-right">{answers}</dd>
              <dt className="text-[color:var(--ink-2)]">{t("my.classes.objectives")}</dt>
              <dd className="num text-right">
                {assessed}
                {" / "}
                {c.los.length}
              </dd>
              {hw.length > 0 && (
                <>
                  <dt className="text-[color:var(--ink-2)]">{t("my.classes.homework")}</dt>
                  <dd className="num text-right">
                    {doneHw}
                    {" / "}
                    {hw.length}
                  </dd>
                </>
              )}
            </dl>
            <p className="mt-2 text-xs text-[color:var(--ink-2)]">
              {c.release_mode === "automatic" ? t("my.classes.releaseAuto") : t("my.classes.releaseCount", { count: c.released_sessions })}
            </p>
            <p className="mt-2">
              <Link to="/my/progress" className="inline-flex min-h-[var(--control-h)] items-center text-sm underline">
                {t("my.classes.seeProgress")}
              </Link>
            </p>
          </section>
        );
      })}
      <p className="text-xs text-[color:var(--ink-2)]">{t("my.classes.privacy")}</p>
    </Shell>
  );
}

export function MyProgress() {
  const { t, i18n } = useTranslation();
  const mine = useMine();
  const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso)) : "");

  return (
    <Shell title={t("my.progress.title")} lede={t("my.progress.lede")}>
      {mine.isPending && <Note>{t("app.loading")}</Note>}
      {mine.isError && <Note>{t("my.classes.noPrincipal")}</Note>}
      {mine.data?.courses.length === 0 && <Note>{t("my.progress.empty")}</Note>}

      {mine.data?.courses.map((c) => {
        const bands = { secure: 0, developing: 0, not_yet: 0, unassessed: 0 };
        for (const lo of c.los) bands[lo.band as keyof typeof bands] += 1;
        return (
          <section key={c.course_id} className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
            <h2 className="text-lg font-semibold break-words">{c.title || t("my.classes.untitled")}</h2>
            <p className="mb-3 text-sm text-[color:var(--ink-2)]">
              {t("me.summary", { secure: bands.secure, developing: bands.developing, notYet: bands.not_yet, total: c.los.length })}
            </p>
            <p className="mb-3 text-xs text-[color:var(--ink-2)]">
              {c.release_mode === "automatic" ? t("my.progress.asItHappens") : t("me.released", { count: c.released_sessions })}
            </p>
            <ul className="flex flex-col gap-2">
              {c.los.map((lo) => (
                <li key={lo.lo_id} className="flex items-start gap-3">
                  <span aria-hidden className="w-4 text-lg leading-6" style={{ color: tone[lo.band] }}>
                    {glyph[lo.band]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words">
                      <span className="mr-2 font-mono text-xs text-[color:var(--ink-2)]">{lo.code}</span>
                      {lo.text}
                    </p>
                    <p className="text-xs text-[color:var(--ink-2)]">
                      {t(`me.bands.${lo.band}`)}
                      {lo.n > 0 && ` · ${t("me.evidence", { count: lo.n })}`}
                      {lo.last_assessed_at && ` · ${fmt(lo.last_assessed_at)}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <p className="text-xs text-[color:var(--ink-2)]">{t("me.legend")}</p>
      <p className="text-xs text-[color:var(--ink-2)]">{t("my.progress.gate")}</p>
    </Shell>
  );
}
